/**
 * Session 37 · Phase 2 · Journey Tracker
 *
 * Vollständiges Client-side Session-Tracking:
 *  - Persistente Session-ID (localStorage, 30-min Inaktivitäts-Rollover)
 *  - Aktive Zeit-Messung (visibilitychange, focus, blur, click/scroll/key/touch)
 *  - Heartbeat alle 15 s wenn aktiv
 *  - SPA-Routenwechsel-Flush
 *  - sendBeacon bei pagehide
 *  - Consent-Bindung (ohne analytics-Consent kein Netzwerk-Call)
 *  - X-Journey-Session-Id-Header für API-Calls
 *
 * WICHTIG: Alle Fehler werden verschluckt: Tracking darf UX niemals stören.
 */

import { readConsent } from "@/lib/consent";
import { ACTIONS } from "./actions";
import { capturePosthog } from "@/lib/tracking/posthog";

const SID_KEY = "pb24_journey_sid";
const LAST_ACTIVE_KEY = "pb24_journey_last_active";
const INACTIVITY_MS = 30 * 60 * 1000;      // 30 min → neue Session
const HEARTBEAT_INTERVAL_MS = 15 * 1000;   // 15 s
const ACTIVITY_TIMEOUT_MS = 30 * 1000;     // 30 s ohne Interaktion → inaktiv
const SCROLL_STEPS = [25, 50, 75, 100];

const API_BASE = (typeof process !== "undefined" && process.env.REACT_APP_BACKEND_URL) || "";

// ---------- Internal state ----------
let _sessionId = null;
let _currentPath = null;
let _currentPathLabel = null;
let _pendingActions = [];
let _activeMsPending = 0;
let _lastActivityAt = 0;
let _lastFlushAt = 0;
let _hbInterval = null;
let _visible = true;
let _focused = true;
let _scrollFiredForPath = new Set();
let _sourceHint = null;
let _deviceHint = null;
let _bootDone = false;

// ---------- Public: current session id (for header injection) ----------
export function getSessionId() {
  return _sessionId;
}

// ---------- Consent guard ----------
function hasAnalyticsConsent() {
  try {
    const c = readConsent();
    return !!(c && c.analytics);
  } catch (_) {
    return false;
  }
}

function currentConsent() {
  try {
    const c = readConsent();
    return { analytics: !!(c && c.analytics), marketing: !!(c && c.marketing) };
  } catch (_) {
    return { analytics: false, marketing: false };
  }
}

// ---------- Session id lifecycle ----------
function _genSid() {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (_) { /* noop */ }
  return `sid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function _ensureSession() {
  // TDDDG § 25: ohne Statistik-Einwilligung nichts im Browser speichern, Kennung nur im Arbeitsspeicher (01.11.2026)
  try { if (!currentConsent().analytics) { if (!_sessionId) _sessionId = _genSid(); return _sessionId; } } catch (_) { /* noop */ }
  try {
    const now = Date.now();
    const lastActive = parseInt(localStorage.getItem(LAST_ACTIVE_KEY) || "0", 10) || 0;
    let sid = localStorage.getItem(SID_KEY);
    if (!sid || (lastActive && now - lastActive > INACTIVITY_MS)) {
      sid = _genSid();
      localStorage.setItem(SID_KEY, sid);
    }
    localStorage.setItem(LAST_ACTIVE_KEY, String(now));
    _sessionId = sid;
    return sid;
  } catch (_) {
    // localStorage unavailable → in-memory fallback
    if (!_sessionId) _sessionId = _genSid();
    return _sessionId;
  }
}

function _touchLastActive() {
  try { if (!currentConsent().analytics) return; } catch (_) { return; }
  try { localStorage.setItem(LAST_ACTIVE_KEY, String(Date.now())); } catch (_) { /* noop */ }
}

// ---------- Source / Device Hints ----------
function _detectSource() {
  try {
    const q = new URLSearchParams(window.location.search || "");
    return {
      utm_source: q.get("utm_source") || "",
      utm_medium: q.get("utm_medium") || "",
      utm_campaign: q.get("utm_campaign") || "",
      utm_content: q.get("utm_content") || "",
      utm_term: q.get("utm_term") || "",
      fbclid: q.get("fbclid") || "",
      gclid: q.get("gclid") || "",
      referrer: document.referrer || "",
    };
  } catch (_) {
    return {};
  }
}

function _detectDevice() {
  try {
    const ua = (navigator.userAgent || "").toLowerCase();
    const isMobile = /iphone|ipod|android.*mobile|windows phone/.test(ua);
    let browser = "Other", browserVersion = "";
    if (ua.includes("edg/")) browser = "Edge";
    else if (ua.includes("chrome")) browser = "Chrome";
    else if (ua.includes("safari") && !ua.includes("chrome")) browser = "Safari";
    else if (ua.includes("firefox")) browser = "Firefox";
    let os = "Other";
    if (ua.includes("windows")) os = "Windows";
    else if (ua.includes("mac os")) os = "macOS";
    else if (ua.includes("android")) os = "Android";
    else if (ua.includes("iphone") || ua.includes("ipad")) os = "iOS";
    else if (ua.includes("linux")) os = "Linux";
    return { browser, browser_version: browserVersion, os, is_mobile: isMobile };
  } catch (_) {
    return {};
  }
}

// ---------- Path-Label mapping (mirror backend) ----------
const _PATH_LABELS = [
  [/^\/?$/, "Startseite"],
  [/^\/privatkunden/, "Privatkunden"],
  [/^\/businesskunden/, "Businesskunden"],
  [/^\/produkte/, "Produkte"],
  [/^\/buchen/, "Verfügbarkeit prüfen"],
  [/^\/buchung\/erfolg/, "Verbindlich gebucht"],
  [/^\/ablauf/, "Ablauf"],
  [/^\/layouts\/designer/, "Layout-Designer"],
  [/^\/layouts/, "Fotolayouts"],
  [/^\/faq/, "FAQ"],
  [/^\/kontakt/, "Kontakt"],
  [/^\/impressum/, "Impressum"],
  [/^\/datenschutz/, "Datenschutz"],
  [/^\/agb/, "AGB"],
  [/^\/kundenkonto\/anmelden/, "Kunden-Login"],
  [/^\/kundenkonto\/registrieren/, "Kunden-Registrierung"],
  [/^\/kundenkonto\/passwort-vergessen/, "Passwort vergessen"],
  [/^\/kundenkonto\/passwort-neu/, "Passwort zurücksetzen"],
  [/^\/kundenkonto\/quick-signup/, "Schnellanmeldung"],
  [/^\/kundenkonto/, "Kundenkonto"],
  [/^\/interne-buchung/, "Interne Buchung (POS)"],
  [/^\/admin/, "Admin-Bereich"],
  [/^\/angebot\/annehmen/, "Angebot annehmen"],
  [/^\/newsletter\/abmelden/, "Newsletter abmelden"],
];

function labelForPath(path) {
  const p = (path || "/").split("?")[0].split("#")[0];
  for (const [re, label] of _PATH_LABELS) {
    if (re.test(p)) return label;
  }
  return p;
}

// ---------- Active-Time accounting ----------
function _isActive() {
  return _visible && _focused && (Date.now() - _lastActivityAt < ACTIVITY_TIMEOUT_MS);
}

function _accumulateActiveTime() {
  if (!_isActive()) return;
  const now = Date.now();
  if (!_lastFlushAt) { _lastFlushAt = now; return; }
  const delta = now - _lastFlushAt;
  _lastFlushAt = now;
  if (delta > 0 && delta < 60000) {
    _activeMsPending += delta;
  }
  _touchLastActive();
}

function _bumpActivity() {
  _lastActivityAt = Date.now();
  if (!_lastFlushAt) _lastFlushAt = _lastActivityAt;
  _accumulateActiveTime();
}

// ---------- Heartbeat network ----------
async function _sendHeartbeat({ synchronous = false } = {}) {
  if (!_sessionId) return;
  // Session 39 · Admin-Bereich niemals tracken.
  if (_currentPath && (_currentPath.startsWith("/admin") || _currentPath.startsWith("/interne-buchung"))) return;
  const consent = currentConsent();
  if (!consent.analytics) return;   // Kein Netzwerk-Call ohne Consent
  _accumulateActiveTime();

  const path = _currentPath || (window.location.pathname + window.location.search);
  const pathFull = window.location.pathname + window.location.search + window.location.hash;
  const label = _currentPathLabel || labelForPath(path);
  const actions = _pendingActions.splice(0);
  const activeDelta = _activeMsPending;
  _activeMsPending = 0;

  const scrollPct = Math.max(...([0, ...Array.from(_scrollFiredForPath).map((s) => parseInt(String(s).split(":")[1] || "0", 10))]));

  const payload = {
    session_id: _sessionId,
    path,
    path_full: pathFull,
    path_label: label,
    active_ms_delta: activeDelta,
    scroll_depth_pct: scrollPct,
    actions,
    source_hint: _sourceHint || _detectSource(),
    device_hint: _deviceHint || _detectDevice(),
    consent,
  };

  try {
    const url = `${API_BASE}/api/journey/heartbeat`;
    if (synchronous && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } else {
      await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: "omit",
      });
    }
  } catch (_) { /* niemals blockieren */ }
}

function _sendEnd() {
  if (!_sessionId) return;
  const consent = currentConsent();
  if (!consent.analytics) return;
  _accumulateActiveTime();

  const payload = {
    session_id: _sessionId,
    active_ms_delta: _activeMsPending,
    actions: _pendingActions.splice(0),
  };
  _activeMsPending = 0;

  try {
    const url = `${API_BASE}/api/journey/end`;
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } else {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: "omit",
      }).catch(() => {});
    }
  } catch (_) { /* noop */ }
}

// ---------- Interaction listeners ----------
function _setupInteractionListeners() {
  const bump = () => _bumpActivity();
  // Throttled mousemove
  let mouseMoveT = 0;
  document.addEventListener("mousemove", () => {
    const now = Date.now();
    if (now - mouseMoveT > 5000) { mouseMoveT = now; bump(); }
  }, { passive: true });
  document.addEventListener("click", bump, { passive: true });
  document.addEventListener("keydown", bump);
  document.addEventListener("touchstart", bump, { passive: true });

  // Scroll depth
  let scrollT = 0;
  window.addEventListener("scroll", () => {
    _bumpActivity();
    const now = Date.now();
    if (now - scrollT < 250) return;
    scrollT = now;
    try {
      const de = document.documentElement;
      const scrollTop = window.scrollY || de.scrollTop || 0;
      const viewport = window.innerHeight || de.clientHeight;
      const height = de.scrollHeight - viewport;
      if (height <= 0) return;
      const pct = Math.round((scrollTop / height) * 100);
      for (const step of SCROLL_STEPS) {
        const key = `${_currentPath}:${step}`;
        if (pct >= step && !_scrollFiredForPath.has(key)) {
          _scrollFiredForPath.add(key);
          const actionId = `scroll_${step}`;
          trackAction({ id: actionId, label: `Scrolltiefe ${step} %`, extra: { scroll_pct: step } });
        }
      }
    } catch (_) { /* noop */ }
  }, { passive: true });

  document.addEventListener("visibilitychange", () => {
    _visible = document.visibilityState === "visible";
    if (!_visible) {
      _accumulateActiveTime();
      _sendHeartbeat({ synchronous: false });
      _lastFlushAt = 0;
    } else {
      _lastActivityAt = Date.now();
      _lastFlushAt = _lastActivityAt;
    }
  });
  window.addEventListener("focus", () => { _focused = true; _lastActivityAt = Date.now(); _lastFlushAt = _lastActivityAt; });
  window.addEventListener("blur", () => {
    _focused = false;
    _accumulateActiveTime();
    _sendHeartbeat({ synchronous: false });
    _lastFlushAt = 0;
  });
  window.addEventListener("pagehide", () => { flushContactSnapshotSync(); _sendEnd(); });
  window.addEventListener("beforeunload", () => { flushContactSnapshotSync(); _sendEnd(); });
}

// ---------- Public API ----------

/**
 * Muss einmal beim App-Mount aufgerufen werden.
 */
export function initJourneyTracker() {
  if (_bootDone) return;
  _bootDone = true;
  _ensureSession();
  _sourceHint = _detectSource();
  _deviceHint = _detectDevice();
  _lastActivityAt = Date.now();
  _lastFlushAt = _lastActivityAt;
  _setupInteractionListeners();

  // Heartbeat-Interval
  _hbInterval = setInterval(() => {
    _accumulateActiveTime();
    if (_activeMsPending > 0 || _pendingActions.length > 0) {
      _sendHeartbeat({ synchronous: false });
    }
  }, HEARTBEAT_INTERVAL_MS);

  // Initial page view heartbeat
  const p = window.location.pathname + window.location.search;
  onRouteChange(p);
}

/**
 * Wird bei jedem SPA-Routenwechsel aufgerufen (vom Provider).
 */
export function onRouteChange(pathWithSearch) {
  _ensureSession();
  const clean = (pathWithSearch || "/").split("?")[0].split("#")[0];
  // Session 39 · Admin-Bereich niemals tracken, Admin-Nutzung soll die
  // öffentliche Statistik nicht verfälschen.
  if (clean.startsWith("/admin") || clean.startsWith("/interne-buchung")) {
    _currentPath = null;
    _currentPathLabel = null;
    return;
  }
  // Vor Wechsel: alte Page finalisieren
  _accumulateActiveTime();
  if (_currentPath && _currentPath !== clean && (_activeMsPending > 0 || _pendingActions.length > 0)) {
    _sendHeartbeat({ synchronous: false });
  }
  _currentPath = clean;
  _currentPathLabel = labelForPath(clean);
  // Neue Page: initialer Heartbeat sofort (ohne active_ms), damit Server die Page-View öffnet
  _lastActivityAt = Date.now();
  _lastFlushAt = _lastActivityAt;
  _sendHeartbeat({ synchronous: false });
}

/**
 * Registriert eine Action (Klick, Form-Field etc.). Wird beim nächsten Heartbeat gesendet.
 * @param {{id: string, label?: string, extra?: object}} action
 */
export function trackAction(action) {
  if (!action || !action.id) return;
  try {
    _pendingActions.push({
      event_id: action.id,
      label: action.label || action.id,
      path: _currentPath || window.location.pathname,
      ts: new Date().toISOString(),
      extra: action.extra || {},
    });
    // Bei wichtigen Aktionen sofort flushen (nicht bei Scroll)
    if (!action.id.startsWith("scroll_")) {
      // kleinen Debounce (300 ms)
      clearTimeout(_flushTimer);
      _flushTimer = setTimeout(() => _sendHeartbeat({ synchronous: false }), 400);
    }
  } catch (_) { /* noop */ }
}

let _flushTimer = null;

/**
 * Convenience-Wrapper für die Action-Registry.
 */
export function track(actionDef, extra) {
  if (!actionDef || !actionDef.id) return;
  trackAction({ id: actionDef.id, label: actionDef.label, extra });
  // Buchungs-/Anfrageschritte auch an PostHog (Trichter-Auswertung, Kevin 28.10.2026)
  if (!actionDef.id.startsWith("scroll_")) capturePosthog(actionDef.id, extra);
}

export { ACTIONS };

// ---------- Session 40 · Contact Snapshot ----------
let _snapshotBuffer = {};
let _snapshotTimer = null;
let _snapshotLastError = null;

/**
 * Sendet begonnene Kontaktdaten mit 1.5s Debounce. Nur Whitelist-Felder.
 * Ohne Analytics-Consent kein Call.
 * @param {object} fields
 * @param {{field:string, reason:string}} [validationError]
 */
export function updateContactSnapshot(fields = {}, validationError = null) {
  try {
    _snapshotBuffer = { ..._snapshotBuffer, ...(fields || {}) };
    if (validationError && validationError.field) _snapshotLastError = validationError;
    if (_snapshotTimer) clearTimeout(_snapshotTimer);
    _snapshotTimer = setTimeout(() => _flushSnapshot(), 1500);
  } catch (_) { /* noop */ }
}

/** Erzwingt sofortiges Flushen (z.B. vor Submit oder pagehide). */
export function flushContactSnapshot(sync = false) {
  try {
    if (_snapshotTimer) { clearTimeout(_snapshotTimer); _snapshotTimer = null; }
    _flushSnapshot({ synchronous: sync });
  } catch (_) { /* noop */ }
}

function flushContactSnapshotSync() {
  try {
    if (_snapshotTimer) { clearTimeout(_snapshotTimer); _snapshotTimer = null; }
    _flushSnapshot({ synchronous: true });
  } catch (_) { /* noop */ }
}

async function _flushSnapshot({ synchronous = false } = {}) {
  const sid = _sessionId || localStorage.getItem(SID_KEY);
  if (!sid) return;
  const consent = currentConsent();
  if (!consent.analytics) return;
  if (Object.keys(_snapshotBuffer).length === 0 && !_snapshotLastError) return;

  const payload = {
    session_id: sid,
    fields: _snapshotBuffer,
    last_validation_error: _snapshotLastError || null,
  };

  const url = `${API_BASE}/api/journey/contact-snapshot`;
  try {
    if (synchronous && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(url, blob);
    } else {
      await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: "omit",
      });
    }
  } catch (_) { /* niemals blockieren */ }
}

/** Zurücksetzen z. B. nach erfolgreicher Buchung, damit nicht weiter gesendet wird. */
export function clearContactSnapshot() {
  _snapshotBuffer = {};
  _snapshotLastError = null;
  if (_snapshotTimer) { clearTimeout(_snapshotTimer); _snapshotTimer = null; }
}
